import React, { useState, useEffect } from 'react';
import { Head, Link } from '@inertiajs/react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { PencilIcon, TrashIcon, CheckIcon, XIcon, MessageCircleIcon } from 'lucide-react';

interface Chat {
  id: number;
  name: string | null;
  display_name: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
}

const getCsrfToken = () => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
};

export default function ChatDashboard() {
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchChats();
  }, []);

  const fetchChats = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/chats', {
        headers: {
          'X-CSRF-Token': getCsrfToken(),
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setChats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'No messages yet';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'No messages yet';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateTime(dateString);
  };

  const handleStartEdit = (chat: Chat) => {
    setEditingId(chat.id);
    setEditValue(chat.name || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleSaveEdit = async (chatId: number) => {
    try {
      const response = await fetch(`/api/chats/${chatId}/name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ name: editValue }),
      });

      if (!response.ok) {
        throw new Error('Failed to update chat name');
      }

      const data = await response.json();
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, name: data.name, display_name: data.display_name }
          : chat
      ));
      setEditingId(null);
      setEditValue('');
    } catch (err) {
      console.error('Failed to update chat name:', err);
    }
  };

  const handleDelete = async (chatId: number) => {
    try {
      const response = await fetch(`/api/chats/${chatId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete chat');
      }

      setChats(prev => prev.filter(chat => chat.id !== chatId));
      setDeletingId(null);
    } catch (err) {
      console.error('Failed to delete chat:', err);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Head title="Chat Dashboard" />
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">Chat Dashboard</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Head title="Chat Dashboard" />
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">Chat Dashboard</h1>
          <Card>
            <CardContent className="pt-6">
              <p className="text-red-600">Error loading chats: {error}</p>
              <Button onClick={fetchChats} className="mt-4">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Head title="Chat Dashboard" />
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Chat Dashboard</h1>
          <Link href="/chats/new">
            <Button>Start New Chat</Button>
          </Link>
        </div>

        {chats.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground mb-4">No chats found. Start your first conversation!</p>
              <Link href="/chats/new">
                <Button>Start New Chat</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chats.map((chat) => (
              <Card key={chat.id} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {editingId === chat.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(chat.id);
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                            className="flex-1 px-2 py-1 text-lg font-semibold bg-background border rounded focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                            autoFocus
                            placeholder="Chat name..."
                          />
                          <Button
                            size="icon-xs"
                            variant="ghost"
                            onClick={() => handleSaveEdit(chat.id)}
                            title="Save"
                          >
                            <CheckIcon className="size-3" />
                          </Button>
                          <Button
                            size="icon-xs"
                            variant="ghost"
                            onClick={handleCancelEdit}
                            title="Cancel"
                          >
                            <XIcon className="size-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <CardTitle className="text-lg truncate flex items-center gap-2">
                            <MessageCircleIcon className="size-4 text-muted-foreground" />
                            {chat.display_name}
                          </CardTitle>
                          <button
                            onClick={() => handleStartEdit(chat)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
                            title="Edit chat name"
                          >
                            <PencilIcon className="size-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <CardDescription className="mt-1">
                    {formatRelativeTime(chat.last_message_at || chat.created_at)}
                  </CardDescription>
                </CardHeader>
                <CardFooter className="pt-0">
                  <div className="flex gap-2 w-full">
                    <Link href={`/chats/${chat.id}`} className="flex-1">
                      <Button variant="outline" className="w-full">Continue Chat</Button>
                    </Link>
                    {deletingId === chat.id ? (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(chat.id)}
                          title="Confirm delete"
                        >
                          <TrashIcon className="size-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeletingId(null)}
                          title="Cancel"
                        >
                          <XIcon className="size-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeletingId(chat.id)}
                        title="Delete chat"
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    )}
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
